/**
 * Shared real-user monitoring helpers for stockstotrade.com and
 * timothysykes.com. Both sites emit the same event schema:
 *
 *   web_vital, navigation_timing, route_navigation,
 *   resource_load_error, http_error
 *
 * Core Web Vitals are document-lifecycle measurements (one LCP / INP / CLS /
 * TTFB / FCP per full page load). Client-side route changes are recorded
 * separately as route_navigation and are not reported as Core Web Vitals.
 */

export type VitalRating = "good" | "needs-improvement" | "poor";

export type PageContext = {
	path: string;
	route: string;
	type: string;
};

const PATTERNS: Array<{ re: RegExp; route: string; type: string }> = [
	{ re: /^\/news\/page\/[^/]+\/$/, route: "/news/page/:page", type: "news" },
	{ re: /^\/news\/$/, route: "/news/", type: "news" },
	{ re: /^\/news\/.+/, route: "/news/:slug", type: "news" },
	{ re: /^\/tim-news\/$/, route: "/tim-news/", type: "news" },
	{ re: /^\/tim-news\/.+/, route: "/tim-news/:slug", type: "news" },
	{
		re: /^\/blog\/author\/[^/]+\/$/,
		route: "/blog/author/:author",
		type: "blog"
	},
	{
		re: /^\/blog\/tim-video\/[^/]+\/$/,
		route: "/blog/tim-video/:slug",
		type: "blog"
	},
	{ re: /^\/blog\/page\/[^/]+\/$/, route: "/blog/page/:page", type: "blog" },
	{ re: /^\/blog\/$/, route: "/blog/", type: "blog" },
	{ re: /^\/blog\/.+/, route: "/blog/:slug", type: "blog" },
	{ re: /^\/quote\/[^/]+\/$/, route: "/quote/:symbol", type: "stock" },
	{ re: /^\/tag\/[^/]+\/$/, route: "/tag/:slug", type: "tag" },
	{
		re: /^\/category\/[^/]+\/page\/[^/]+\/$/,
		route: "/category/:slug/page/:page",
		type: "category"
	},
	{ re: /^\/category\/[^/]+\/$/, route: "/category/:slug", type: "category" },
	{ re: /^\/authors\/[^/]+\/$/, route: "/authors/:author", type: "author" },
	{ re: /^\/scans\/$/, route: "/scans/", type: "watchlist" },
	{ re: /^\/scans\/[^/]+\/$/, route: "/scans/:scan", type: "watchlist" },
	{ re: /^\/search\/$/, route: "/search/", type: "search" },
	{
		re: /^\/stocks-to-watch\/$/,
		route: "/stocks-to-watch/",
		type: "watchlist"
	},
	{
		re: /^\/stocks-to-watch\/.+/,
		route: "/stocks-to-watch/:slug",
		type: "watchlist"
	},
	{ re: /^\/embed\/.+/, route: "/embed/:slug", type: "other" },
	{ re: /^\/preview\/.+/, route: "/preview/:slug", type: "other" }
];

const LANDING_PAGES = new Set([
	"/about/",
	"/affiliate-disclosure/",
	"/ai-disclosure/",
	"/aizen-preview/",
	"/app/",
	"/artificial-intelligence-disclosure/",
	"/broker-integration/",
	"/careers/",
	"/ccpa-policy/",
	"/code-of-conduct/",
	"/contact/",
	"/cookie-policy/",
	"/disclaimer/",
	"/disclosure-of-related-and-partner-entities/",
	"/dmca-takedown-policy/",
	"/earnings-claims-disclosure/",
	"/editorial-guidelines/",
	"/member-terms/",
	"/millionaire-challenge/",
	"/plans/",
	"/platform/",
	"/privacy-policy/",
	"/reviews/",
	"/sitemap/",
	"/system-requirements/",
	"/terms-of-service/",
	"/terms-service-use-agreement/",
	"/top-features/",
	"/trading-hw-1/",
	"/trading-hw-2/",
	"/trading-hw-3/",
	"/trading-hw-4/",
	"/trading-hw-5/",
	"/trading-hw-bonus/"
]);

const SAMPLE_KEY = "rum_session_sample";

export function normalizePath(input: string): string {
	let path = input || "/";
	try {
		path = new URL(input, "https://local.invalid").pathname;
	} catch {
		path = String(input).split(/[?#]/)[0] || "/";
	}
	if (!path.startsWith("/")) path = `/${path}`;
	path = path.replace(/\/{2,}/g, "/");
	if (path.length > 1 && path.endsWith("/")) {
		path = path.replace(/\/+$/, "/");
	}
	if (path.length > 1 && !path.endsWith("/")) path += "/";
	return path || "/";
}

export function pageContext(input: string): PageContext {
	const path = normalizePath(input);
	if (path === "/") return { path, route: "/", type: "homepage" };

	for (const pattern of PATTERNS) {
		if (pattern.re.test(path)) {
			return { path, route: pattern.route, type: pattern.type };
		}
	}

	if (LANDING_PAGES.has(path)) {
		return { path, route: path, type: "landing-page" };
	}

	const parts = path.split("/").filter(Boolean);
	if (parts.length <= 1) {
		return { path, route: "/:slug", type: "landing-page" };
	}
	return { path, route: `/${parts[0]}/:slug`, type: "landing-page" };
}

export function safePageUrl(href: string): string {
	try {
		const url = new URL(href);
		if (url.protocol !== "http:" && url.protocol !== "https:") return "";
		const host = url.hostname.toLowerCase();
		const allowed =
			host === "localhost" ||
			host === "127.0.0.1" ||
			host === "stockstotrade.com" ||
			host === "www.stockstotrade.com" ||
			host === "timothysykes.com" ||
			host === "www.timothysykes.com" ||
			host.endsWith(".workers.dev") ||
			host.endsWith(".pages.dev");
		if (!allowed) return "";
		return `${url.origin}${normalizePath(url.pathname)}`;
	} catch {
		return "";
	}
}

export function sanitizeRequestUrl(input: string): string {
	if (!input) return "";
	try {
		const url = new URL(input, "https://local.invalid");
		if (url.protocol === "javascript:" || url.protocol === "data:") return "";
		const path = normalizePath(url.pathname);
		if (url.host === "local.invalid") return path;
		return `${url.origin}${path}`;
	} catch {
		return String(input).split(/[?#]/)[0].slice(0, 300);
	}
}

export function resourceType(
	tagName: string,
	rel = "",
	asAttr = ""
): string {
	const tag = (tagName || "").toLowerCase();
	const relValue = rel.toLowerCase();
	const asValue = asAttr.toLowerCase();
	if (tag === "img" || tag === "image" || tag === "picture") return "image";
	if (tag === "script") return "script";
	if (tag === "iframe") return "iframe";
	if (tag === "link") {
		if (relValue.includes("stylesheet") || asValue === "style") {
			return "stylesheet";
		}
		if (relValue.includes("font") || asValue === "font") return "font";
	}
	if (asValue === "font") return "font";
	return "other";
}

export function rateVital(metric: string, value: number): VitalRating {
	if (metric === "CLS") {
		if (value <= 0.1) return "good";
		if (value <= 0.25) return "needs-improvement";
		return "poor";
	}
	if (metric === "INP") {
		if (value <= 200) return "good";
		if (value <= 500) return "needs-improvement";
		return "poor";
	}
	if (metric === "LCP") {
		if (value <= 2500) return "good";
		if (value <= 4000) return "needs-improvement";
		return "poor";
	}
	return "good";
}

export function formatVitalValue(metric: string, value: number): string {
	if (!Number.isFinite(value) || value < 0) return "0";
	if (metric === "CLS") return value.toFixed(4);
	return String(Math.round(value));
}

export function resolveEnvironment(
	configured: string | undefined,
	hostname: string
): string {
	const host = (hostname || "").toLowerCase();
	if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") {
		return "local";
	}
	if (
		host.includes("staging") ||
		host.endsWith(".workers.dev") ||
		host.endsWith(".pages.dev")
	) {
		return "staging";
	}
	const value = (configured || "").toLowerCase();
	if (value === "local" || value === "staging" || value === "production") {
		return value;
	}
	if (value === "dev" || value === "development" || value === "test") {
		return "staging";
	}
	return "production";
}

export function shortVersion(raw: string | undefined): string {
	const value = (raw || "").trim();
	if (!value) return "unknown";
	if (/^[0-9a-f]{7,40}$/i.test(value)) return value.slice(0, 7);
	return value.slice(0, 32);
}

export function parseEnabled(raw: unknown): boolean {
	if (raw === undefined || raw === null || raw === "") return true;
	if (raw === false || raw === "false" || raw === "0") return false;
	return true;
}

export function parseSampleRate(raw: unknown, environment: string): number {
	if (raw === undefined || raw === null || raw === "") {
		return environment === "production" ? 0.05 : 1;
	}
	const rate = Number(raw);
	if (!Number.isFinite(rate)) return environment === "production" ? 0.05 : 1;
	return Math.min(1, Math.max(0, rate));
}

export function sessionSampled(
	rate: number,
	storage: Pick<Storage, "getItem" | "setItem"> | null
): boolean {
	if (rate >= 1) return true;
	if (rate <= 0) return false;
	try {
		const existing = storage?.getItem(SAMPLE_KEY);
		if (existing === "1") return true;
		if (existing === "0") return false;
		const sampled = Math.random() < rate;
		storage?.setItem(SAMPLE_KEY, sampled ? "1" : "0");
		return sampled;
	} catch {
		return Math.random() < rate;
	}
}

export function siteFromService(serviceName: string): string {
	if (
		serviceName === "timothysykes-web" ||
		serviceName === "ts-web" ||
		serviceName.startsWith("timothy")
	) {
		return "timothysykes";
	}
	return "stockstotrade";
}

export function browserName(userAgent: string): string {
	if (/Edg\//.test(userAgent)) return "Edge";
	if (/Firefox\//.test(userAgent)) return "Firefox";
	if (/Chrome\//.test(userAgent) && !/Chromium/.test(userAgent)) return "Chrome";
	if (/Safari\//.test(userAgent)) return "Safari";
	return "other";
}

export function browserVersion(userAgent: string): string {
	const match = userAgent.match(
		/(?:Edg|Firefox|Chrome|Version)\/(\d+(?:\.\d+)?)/
	);
	return match?.[1] || "";
}

export function osName(userAgent: string): string {
	if (/Windows/.test(userAgent)) return "Windows";
	if (/Android/.test(userAgent)) return "Android";
	if (/iPhone|iPad|iPod/.test(userAgent)) return "iOS";
	if (/Mac OS X/.test(userAgent)) return "macOS";
	if (/Linux/.test(userAgent)) return "Linux";
	return "other";
}

export function deviceType(
	userAgent: string,
	width: number,
	touchPoints = 0
): "mobile" | "tablet" | "desktop" {
	if (/iPad|Tablet|PlayBook|Silk/i.test(userAgent)) return "tablet";
	if (touchPoints > 1 && /Macintosh/.test(userAgent)) return "tablet";
	if (/Mobi|Android|iPhone|iPod/i.test(userAgent)) return "mobile";
	if (width > 0 && width < 768) return "mobile";
	if (width > 0 && width < 1024) return "tablet";
	return "desktop";
}

export function viewportClass(width: number): string {
	if (width > 0 && width < 768) return "narrow";
	if (width > 0 && width < 1024) return "medium";
	return "wide";
}

export function referrerCategory(referrer: string, hostname: string): string {
	if (!referrer) return "direct";
	try {
		const host = new URL(referrer).hostname.toLowerCase();
		if (host === hostname.toLowerCase()) return "internal";
		if (/google\.|bing\.|duckduckgo\.|yahoo\.|search\./.test(host)) {
			return "search";
		}
		return "external";
	} catch {
		return "external";
	}
}

export function attrs(
	entries: Record<string, string | undefined>
): Record<string, string> {
	const out: Record<string, string> = {};
	for (const [key, value] of Object.entries(entries)) {
		if (value) out[key] = value;
	}
	return out;
}
